import { IGame, ITeam, ITeamSchedule } from "@src/types/api";

import getOpponentInfo from "@Leagues/lib/getOpponentInfo";
import { UseTeamGamesReturn } from "@Leagues/types/leagues";

const useTeamGames = (
    teamId: ITeam["id"],
    currentDate: Date,
    schedule: ITeamSchedule,
): UseTeamGamesReturn => {
    // Find the most recent game (latest game that has already occurred)
    const mostRecentGame =
        schedule.games
            ?.filter((game: IGame) => game.status === "Scheduled")
            ?.filter((game: IGame) => new Date(game.date) < currentDate)
            .sort(
                (a: IGame, b: IGame) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime(),
            )[0] || null;

    const { isHome } = getOpponentInfo(mostRecentGame, teamId);

    const homeScore = mostRecentGame?.home.score
        ? parseInt(mostRecentGame.home.score.toString())
        : null;
    const visitorScore = mostRecentGame?.visitor.score
        ? parseInt(mostRecentGame.visitor.score.toString())
        : null;

    const result =
        homeScore !== null && visitorScore !== null
            ? isHome
                ? homeScore > visitorScore
                    ? "W"
                    : "L"
                : visitorScore > homeScore
                  ? "W"
                  : "L"
            : null;

    const score =
        homeScore !== null && visitorScore !== null
            ? isHome
                ? result === "W"
                    ? `W ${homeScore}-${visitorScore}`
                    : `L ${visitorScore}-${homeScore}`
                : result === "W"
                  ? `W ${visitorScore}-${homeScore}`
                  : `L ${homeScore}-${visitorScore}`
            : null;

    // Find the next game (earliest game that hasn't occurred yet)
    const nextGame =
        schedule.games
            ?.filter((game: IGame) => game.status === "Scheduled")
            ?.filter((game: IGame) => new Date(game.date) >= currentDate)
            .sort(
                (a: IGame, b: IGame) =>
                    new Date(a.date).getTime() - new Date(b.date).getTime(),
            )[0] || null;

    return {
        recent: { game: mostRecentGame, result, score },
        next: { game: nextGame },
    };
};

export default useTeamGames;
